import { useCallback, useEffect, useMemo, useState } from 'react';

const DISMISSED_AT_KEY = 'installPromptDismissedAt';
const INSTALLED_KEY = 'installPromptInstalled';
const COOLDOWN_DAYS = 14;
const COOLDOWN_MS = COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
const SHOW_DELAY_MS = 1500;

export const isMobileUserAgent = (userAgent = '') => /android|iphone|ipad|ipod/i.test(userAgent);

export const isIosUserAgent = (userAgent = '') => /iphone|ipad|ipod/i.test(userAgent);

export const isIosSafariUserAgent = (userAgent = '') => {
  if (!isIosUserAgent(userAgent)) {
    return false;
  }

  const hasVersionToken = /version\/[\d.]+/i.test(userAgent);
  const hasSafariToken = /safari\/[\d.]+/i.test(userAgent);
  const hasUnsupportedBrowserToken =
    /crios|fxios|edgios|opios|fban|fbav|instagram|line|gsa|duckduckgo|pinterest|snapchat|micromessenger|twitter/i.test(userAgent);

  return hasVersionToken && hasSafariToken && !hasUnsupportedBrowserToken;
};

export const isStandaloneDisplayMode = (win) => {
  try {
    const resolvedWindow = win ?? (typeof window !== 'undefined' ? window : null);
    if (!resolvedWindow) {
      return false;
    }

    const isStandalone = resolvedWindow.matchMedia?.('(display-mode: standalone)')?.matches === true;
    const isIosStandalone = resolvedWindow.navigator?.standalone === true;
    const referrer = resolvedWindow.document?.referrer || '';
    const isAndroidReferrer = referrer.startsWith('android-app://');
    return isStandalone || isIosStandalone || isAndroidReferrer;
  } catch {
    return false;
  }
};

export const isDismissedInCooldown = (storage, nowMs = Date.now()) => {
  try {
    const resolvedStorage = storage ?? (typeof window !== 'undefined' ? window.localStorage : null);
    if (!resolvedStorage) {
      return false;
    }

    const dismissedAt = Number(resolvedStorage.getItem(DISMISSED_AT_KEY));
    if (!Number.isFinite(dismissedAt) || dismissedAt <= 0) {
      return false;
    }

    return nowMs - dismissedAt < COOLDOWN_MS;
  } catch {
    return false;
  }
};

const useInstallPrompt = ({ enabled = true } = {}) => {
  const [deferredPromptEvent, setDeferredPromptEvent] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  const userAgent = useMemo(() => {
    if (typeof navigator === 'undefined') {
      return '';
    }
    return navigator.userAgent || '';
  }, []);

  const isMobile = useMemo(() => isMobileUserAgent(userAgent), [userAgent]);
  const isIosSafari = useMemo(() => isIosSafariUserAgent(userAgent), [userAgent]);
  const isAndroid = useMemo(() => /android/i.test(userAgent), [userAgent]);

  const hasInstalledFlag = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      return window.localStorage.getItem(INSTALLED_KEY) === '1';
    } catch {
      return false;
    }
  }, []);

  const isInstalled = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return hasInstalledFlag || isStandaloneDisplayMode(window);
  }, [hasInstalledFlag]);

  const platform = useMemo(() => {
    if (isIosSafari) {
      return 'ios';
    }

    if (isAndroid && deferredPromptEvent) {
      return 'android';
    }

    return null;
  }, [deferredPromptEvent, isAndroid, isIosSafari]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPromptEvent(event);
    };

    const handleInstalled = () => {
      try {
        window.localStorage.setItem(INSTALLED_KEY, '1');
      } catch {}
      setIsVisible(false);
      setDeferredPromptEvent(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  useEffect(() => {
    if (!enabled || !isMobile || isInstalled || !platform) {
      setIsVisible(false);
      return undefined;
    }

    if (typeof window !== 'undefined' && isDismissedInCooldown(window.localStorage)) {
      setIsVisible(false);
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      setIsVisible(true);
    }, SHOW_DELAY_MS);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [enabled, isInstalled, isMobile, platform]);

  const dismissPrompt = useCallback(() => {
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(DISMISSED_AT_KEY, Date.now().toString());
      } catch {}
    }
    setIsVisible(false);
  }, []);

  const triggerAndroidInstall = useCallback(async () => {
    if (!deferredPromptEvent) {
      return;
    }

    try {
      await deferredPromptEvent.prompt();
      const result = await deferredPromptEvent.userChoice;
      if (result?.outcome === 'accepted' && typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(INSTALLED_KEY, '1');
        } catch {}
      }
    } catch {
      // Keep this silent; users can retry from browser menu.
    } finally {
      setDeferredPromptEvent(null);
      dismissPrompt();
    }
  }, [deferredPromptEvent, dismissPrompt]);

  return {
    isVisible,
    platform,
    dismissPrompt,
    triggerAndroidInstall,
  };
};

export default useInstallPrompt;
